// One definition of how an environment value is read.
//
// A Vercel environment variable picked up a LEADING TAB, observed in production
// 2026-08-24: `EXPO_PUBLIC_SUPABASE_URL` was inlined as "\thttps://…", so the
// widget's `api-base` attribute on /:tenant read "\thttps://…/functions/v1".
//
// It went unnoticed for the worst possible reason — it mostly works. The WHATWG
// URL parser strips tabs and newlines from a URL, so every `fetch()` succeeded and
// nothing looked wrong. What does NOT strip them is an HTTP HEADER value: a tab in
// `EXPO_PUBLIC_SUPABASE_ANON_KEY` rides verbatim into `apikey:` / `Authorization:`
// and the request is rejected with nothing on screen to explain it. The same value
// is also pattern-matched (`EXPO_PUBLIC_TENANT_SLUG` is validated against a slug
// regex before being echoed into a URL), where a stray character is a silent
// mismatch rather than a tolerated one.
//
// So the trim happens ONCE, here, rather than at ten call sites that each have to
// remember. Note this takes the VALUE, never the name: `vite.config.ts` inlines
// `process.env.EXPO_PUBLIC_*` by literal text replacement, so a dynamic
// `process.env[name]` is never substituted and throws in the browser, where
// `process` does not exist.

/**
 * Trim an inlined environment value, treating blank as absent.
 *
 * An empty string is what `vite.config.ts` substitutes for an UNSET variable
 * (`JSON.stringify(value ?? '')`), so a whitespace-only value and a missing one
 * are the same thing and both must read as `undefined` — otherwise `??` fallbacks
 * silently accept `''` as a configured value.
 */
export function envValue(raw: string | undefined): string | undefined {
  const trimmed = raw?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * Same, for a value the app cannot start without. Returns `''` rather than
 * throwing: these are read at MODULE SCOPE, so a throw would blank the whole app
 * — including the sign-in screen that would let somebody report the problem. A
 * misconfigured deployment should fail at the request it actually needs the value
 * for, with a normal error path, not at import time.
 */
export function requiredEnvValue(raw: string | undefined): string {
  return envValue(raw) ?? ''
}
